// Vacation entitlement and balances (admin).
//
// Vacation used to be counted only after the fact, with no allowance and no carry-over — which is
// wrong for anyone joining or leaving mid-year. Entitlement now accrues PRO RATA against real
// employment, so a July starter has earned about half a year's worth by December, and it follows
// the employment record rather than being stored, so correcting a start date corrects the balance.
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';
import HelpPanel from './HelpPanel';

interface Row {
  username: string; display_name: string; days_per_year: number; carried_in: number;
  employed_days_ytd: number; employed_days_full_year: number;
  accrued_ytd: number; accrued_full_year: number; taken: number; adjustments: number;
  remaining_now: number; remaining_full_year: number; has_override: boolean;
}
interface LedgerRow {
  // Holidays taken are read off the calendar rather than stored, so those rows have no id and
  // no author — the calendar is the record.
  id: number | null; entry_date: string; kind: string; days: number; note: string | null;
  created_by: string | null; absence_span: string | null; from_calendar?: boolean;
}

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const btn = 'px-2.5 py-1 text-xs rounded-lg';
const KIND_LABEL: Record<string, string> = {
  taken: 'Taken', accrual: 'Accrued', carry_in: 'Carried in',
  adjustment: 'Adjustment', expired: 'Expired', paid_out: 'Paid out',
};

export default function VacationPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<Row[]>([]);
  const [statutory, setStatutory] = useState(28);
  const [basis, setBasis] = useState('calendar_excl_holidays');
  const [basisOpts, setBasisOpts] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [openLedger, setOpenLedger] = useState<Row | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [backfill, setBackfill] = useState<any | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    const [v, s] = await Promise.all([
      authFetch(`${API}/statistics/vacation?year=${year}`).then(r => r.ok ? r.json() : null),
      authFetch(`${API}/statistics/work-time/settings`).then(r => r.ok ? r.json() : null),
    ]);
    if (!v) { setErr('Could not load balances'); return; }
    setRows(v.people || []);
    setStatutory(v.statutory_days ?? 28);
    if (s) { setBasis(s.vacation_day_basis); setBasisOpts(s.options || {}); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const call = async (url: string, method: string, body?: any) => {
    setBusy(true); setErr(null);
    const r = await authFetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    setBusy(false);
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setErr(m); return null;
    }
    return r.json().catch(() => ({}));
  };

  const saveBasis = async (v: string) => {
    const out = await call(`${API}/statistics/work-time/settings`, 'PUT', { vacation_day_basis: v });
    if (out) { setBasis(v); setMsg('Basis saved. Bookings already made keep the basis they were charged under — re-run the backfill or edit a spell to restate them.'); load(); }
  };

  const showLedger = async (row: Row) => {
    setOpenLedger(row);
    const r = await authFetch(`${API}/statistics/vacation/${encodeURIComponent(row.username)}/ledger?year=${year}`);
    setLedger(r.ok ? (await r.json()).ledger || [] : []);
  };

  const runBackfill = async (dry: boolean) => {
    const out = await call(`${API}/statistics/vacation/backfill`, 'POST', { dry_run: dry });
    if (!out) return;
    if (dry) { setBackfill(out); setMsg(null); }
    else {
      setBackfill(null);
      setMsg(`Booked ${out.created.length} historic vacation spell(s).`);
      load();
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="text-xs text-gray-500">Year
          <input type="number" min={2020} max={2100} className={`${inp} ml-2 w-20`} value={year}
            onChange={e => setYear(parseInt(e.target.value) || year)} />
        </label>
        <span className="text-xs text-gray-600">Statutory default {statutory} days/year</span>
        <label className="text-xs text-gray-500 ml-auto">A vacation day costs
          <select className={`${inp} ml-2`} value={basis} onChange={e => saveBasis(e.target.value)}>
            {Object.entries(basisOpts).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      {err && <div className="mb-3 bg-red-950/40 border border-red-900/60 text-red-300 text-xs rounded-lg px-3 py-2">{err}</div>}
      {msg && <div className="mb-3 bg-[#1a1d27] border border-gray-800 text-xs text-gray-400 rounded-lg px-3 py-2">{msg}</div>}

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="px-3 py-2 text-left font-medium">Person</th>
              <th className="px-3 py-2 text-right font-medium" title="Allowance for a full year">Per year</th>
              <th className="px-3 py-2 text-right font-medium" title="Brought in from last year">Carried in</th>
              <th className="px-3 py-2 text-right font-medium" title="Earned so far, pro rata to time employed">Accrued</th>
              <th className="px-3 py-2 text-right font-medium">Taken</th>
              <th className="px-3 py-2 text-right font-medium" title="Available to take right now">Left now</th>
              <th className="px-3 py-2 text-right font-medium" title="If they stay employed all year">By year end</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.username} className="border-b border-gray-800/50">
                <td className="px-3 py-2.5 text-gray-200">
                  {r.display_name}
                  {r.has_override && <span className="ml-2 text-[10px] text-blue-400/70 uppercase">override</span>}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400">{r.days_per_year}</td>
                <td className="px-3 py-2.5 text-right text-gray-400">{r.carried_in || <span className="text-gray-700">—</span>}</td>
                <td className="px-3 py-2.5 text-right text-gray-300">{r.accrued_ytd}</td>
                <td className="px-3 py-2.5 text-right text-gray-300">{r.taken || <span className="text-gray-700">—</span>}</td>
                <td className={`px-3 py-2.5 text-right font-semibold ${r.remaining_now < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {r.remaining_now}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-500">{r.remaining_full_year}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => showLedger(r)}
                    className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>Ledger</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-600 text-sm">
                Nobody was employed in {year}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Backfill — historic vacation entered day-by-day is invisible to the ledger */}
      <div className="mt-4 bg-[#1a1d27] border border-amber-900/40 rounded-xl p-4">
        <div className="text-amber-400/90 text-xs uppercase tracking-widest font-semibold mb-2">
          Historic vacation
        </div>
        <p className="text-xs text-gray-500">
          “Taken” is read straight off the calendar, so vacation entered day by day already counts
          towards the balances above — nothing here is missing from them. What those loose days do
          not have is a <span className="text-gray-300">spell</span>, which is what the Absences tab
          lists and what carries the notified and certificate detail. This groups them into spells
          (bridging weekends and holidays, so one week off becomes one spell). Tidying only; it will
          not move anyone’s balance.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => runBackfill(true)} disabled={busy}
            className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>
            Preview what it would book
          </button>
          {backfill && backfill.spells_found > 0 && (
            <button onClick={() => runBackfill(false)} disabled={busy}
              className={`${btn} bg-amber-700 hover:bg-amber-600 text-white`}>
              Book these {backfill.spells_found} spell(s)
            </button>
          )}
        </div>
        {backfill && (
          <div className="mt-3 text-xs">
            {backfill.spells_found === 0 ? (
              <span className="text-green-400">Nothing to backfill — all vacation is already booked.</span>
            ) : (
              <>
                <div className="text-gray-400 mb-1">
                  {backfill.spells_found} spell(s), charged as{' '}
                  <span className="text-gray-200">
                    {backfill.basis === 'working_days' ? 'working days' : 'calendar days excl. holidays'}
                  </span>:
                </div>
                {backfill.would_create.map((p: any, i: number) => (
                  <div key={i} className="text-gray-500">
                    · {p.username} {p.start_date}{p.end_date !== p.start_date ? ` → ${p.end_date}` : ''}
                    {' '}({p.working_days} working day{p.working_days === 1 ? '' : 's'})
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {openLedger && (
        <LedgerDrawer row={openLedger} year={year} ledger={ledger} busy={busy}
          onClose={() => { setOpenLedger(null); load(); }} call={call} />
      )}

      <HelpPanel items={[
        { label: 'Accrued', desc: 'Earned so far this year, pro rata to how long they have actually been employed — so someone who started in July has earned about half a full year by December. It is computed from their employment record, not stored, so correcting a start date corrects the balance automatically.' },
        { label: 'Left now vs By year end', desc: '"Left now" is what they may take today. "By year end" assumes they stay employed for the rest of the year. A negative figure means they have taken more than they have earned, which is legitimate but worth knowing.' },
        { label: 'Taken', desc: 'Read straight off the calendar — every day marked as vacation counts, whether it was recorded as an absence, painted across a date range, or typed in one day at a time. Consecutive days are grouped into one holiday, bridging weekends and public holidays, so a fortnight off is charged as one period rather than two. Marking a day on the calendar is all that is needed; there is nothing separate to remember to book.' },
        { label: 'A vacation day costs', desc: 'Calendar days excluding public holidays is the Estonian statutory reading — a week off costs 7 days of entitlement. Working days charges only rostered days, so the same week costs 5. Because "Taken" is derived from the calendar rather than stored, changing this re-charges every holiday in the year under the new rule, past ones included. Decide it once and leave it alone.' },
        { label: 'Ledger', desc: 'The movements behind the balance. Holidays come from the calendar and are shown as "from the calendar"; carry-in, adjustments, pay-outs and expiry are entered here and are append-only, so a mistake is corrected by adding a reversal rather than an edit.' },
        { label: 'Override', desc: 'Shown when someone has a per-person allowance or opening carry-over instead of the statutory default.' },
        { label: 'Preview / Book', desc: 'Always preview first — it writes nothing and shows exactly which spells it would create. Only then does Book make the entries.' },
      ]} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function LedgerDrawer({ row, year, ledger, busy, onClose, call }: {
  row: Row; year: number; ledger: LedgerRow[]; busy: boolean; onClose: () => void;
  call: (u: string, m: string, b?: any) => Promise<any>;
}) {
  const [perYear, setPerYear] = useState(String(row.days_per_year));
  const [carried, setCarried] = useState(String(row.carried_in));
  const [adjDays, setAdjDays] = useState('');
  const [adjKind, setAdjKind] = useState('adjustment');
  const [adjNote, setAdjNote] = useState('');
  const [local, setLocal] = useState<LedgerRow[]>(ledger);
  useEffect(() => { setLocal(ledger); }, [ledger]);

  const reload = async () => {
    const r = await authFetch(`${API}/statistics/vacation/${encodeURIComponent(row.username)}/ledger?year=${year}`);
    if (r.ok) setLocal((await r.json()).ledger || []);
  };

  const saveEnt = async () => {
    await call(`${API}/statistics/vacation/entitlement`, 'PUT', {
      username: row.username, year, days_per_year: parseFloat(perYear) || 0,
      carried_in: parseFloat(carried) || 0,
    });
    reload();
  };
  const addAdj = async () => {
    const d = parseFloat(adjDays);
    if (!isFinite(d) || d === 0) return;
    await call(`${API}/statistics/vacation/adjustment`, 'POST', {
      username: row.username, year, kind: adjKind, days: d, note: adjNote || null,
    });
    setAdjDays(''); setAdjNote('');
    reload();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto py-10"
         onClick={onClose}>
      <div className="bg-[#151822] border border-gray-800 rounded-2xl w-full max-w-xl p-5 space-y-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold">{row.display_name} — vacation {year}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              employed {row.employed_days_ytd} day(s) so far · accrued {row.accrued_ytd} ·
              taken {row.taken} · <span className="text-gray-300">left {row.remaining_now}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-lg leading-none">×</button>
        </div>

        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-2">Allowance</div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-500">Days per year
              <input type="number" step={0.5} className={`${inp} ml-2 w-20`} value={perYear}
                onChange={e => setPerYear(e.target.value)} />
            </label>
            <label className="text-xs text-gray-500">Carried in
              <input type="number" step={0.5} className={`${inp} ml-2 w-20`} value={carried}
                onChange={e => setCarried(e.target.value)} />
            </label>
            <button onClick={saveEnt} disabled={busy}
              className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}>Save</button>
          </div>
        </div>

        <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-2">Add a movement</div>
          <div className="flex flex-wrap items-end gap-2">
            <select className={inp} value={adjKind} onChange={e => setAdjKind(e.target.value)}>
              <option value="adjustment">Adjustment</option>
              <option value="paid_out">Paid out</option>
              <option value="expired">Expired</option>
            </select>
            <label className="text-xs text-gray-500">Days
              <input type="number" step={0.5} className={`${inp} ml-2 w-20`} value={adjDays}
                onChange={e => setAdjDays(e.target.value)} placeholder="+/-" />
            </label>
            <input className={`${inp} flex-1 min-w-[140px]`} value={adjNote}
              onChange={e => setAdjNote(e.target.value)} placeholder="Why (recorded permanently)" />
            <button onClick={addAdj} disabled={busy || !adjDays}
              className={`${btn} bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white`}>Add</button>
          </div>
          <p className="text-[11px] text-gray-600 mt-1.5">
            Positive adds entitlement, negative consumes it. Entries are never edited or deleted —
            correct one by adding its reverse.
          </p>
        </div>

        <div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wide mb-1.5">Movements</div>
          {local.length === 0 ? (
            <p className="text-xs text-gray-600">No movements yet — nothing taken or adjusted.</p>
          ) : (
            <div className="space-y-1 max-h-[40vh] overflow-y-auto">
              {local.map((l, i) => (
                <div key={l.id ?? `cal-${l.entry_date}-${i}`} className="flex items-baseline gap-2 text-xs">
                  <span className="text-gray-600 w-[86px] flex-shrink-0">{l.entry_date}</span>
                  <span className="text-gray-300 w-[84px] flex-shrink-0">{KIND_LABEL[l.kind] || l.kind}</span>
                  <span className={`w-[52px] flex-shrink-0 text-right font-medium ${l.days < 0 ? 'text-amber-400' : 'text-green-400'}`}>
                    {l.days > 0 ? '+' : ''}{l.days}
                  </span>
                  <span className="text-gray-600 truncate">
                    {l.absence_span ? `${l.absence_span} · ` : ''}{l.note || ''}
                    <span className="text-gray-700">
                      {' '}({l.from_calendar ? 'from the calendar' : l.created_by})
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
